/* eslint-disable camelcase, @typescript-eslint/no-use-before-define, no-underscore-dangle */
import { type Properties } from 'posthog-js';
import { useEffect } from 'react';

import { posthogEnvironment } from 'utils/posthogEnvironment';

import { usePostHog } from './usePostHog';

type Whoami = {
  email?: string;
  first_name?: string;
  last_name?: string;
  external_id?: string;
  wx_external_id?: string | null;
  wx_audience_external_id?: string | null;
  account_id?: string;
  account_name?: string;
  account_wx_external_id?: string;
  last_sign_in_at?: string;
  role?: string;
  is_impersonated?: boolean;
};

const whoamiToIdentifyPropertyKeys: Partial<Record<keyof Whoami, string>> = {
  email: 'email',
  first_name: 'firstname',
  last_name: 'lastname',
  external_id: 'hire_external_id',
  account_id: 'account_id',
  last_sign_in_at: 'last_login_hire',
  account_wx_external_id: 'company_uuid',
  wx_external_id: 'user_uuid',
  wx_audience_external_id: 'audience_uuid',
  // Person property from the Session Identify spec (mutable, $set).
  role: 'user_role',
};

// Approximate "app load" timestamp — used as the start point for
// session_bootstrap_ms (time from load to identify/bootstrap completion).
const appLoadedAt = Date.now();

export function usePostHogIdentify(whoami: Whoami | undefined): void {
  const posthog = usePostHog();
  useEffect(() => {
    if (!whoami?.external_id || posthog._isIdentified()) {
      return;
    }

    // Ordering guarantee (spec): identify + group must complete before the
    // hire_session:session_start bootstrap event and before any feature event.
    posthog.identify(whoami.external_id, identifyPropertiesFromWhoami(whoami));

    if (whoami.account_id) {
      // company_name is the source of the `company_name` standard property on
      // every event; it rides on the `account` group rather than being
      // duplicated onto each payload. The group property MUST be keyed
      // `company_name` (the spec/reporting field); `name` is also set so
      // PostHog shows a human-readable label for the account group in its UI.
      posthog.group(
        'account',
        whoami.account_id,
        whoami.account_name
          ? { name: whoami.account_name, company_name: whoami.account_name }
          : undefined,
      );
    }

    // Single per-session bootstrap event (Session Identify & Account Group
    // spec). Captured via the raw client because the typed catalog has no
    // `hire_session` category, same as $pageview. The `_isIdentified()` guard
    // above makes this fire exactly once per session.
    posthog._rawClient.capture('hire_session:session_start', {
      product: 'hire',
      ui: 'recruiter',
      environment: posthogEnvironment,
      user_role: whoami.role,
      account_id: whoami.account_id,
      // Per-session flags. is_impersonated defaults false until support/
      // impersonation detection is wired (spec Open Q4).
      //
      // is_first_session_for_user is intentionally omitted: whoami exposes no
      // reliable prior-login signal (last_sign_in_at is already populated on
      // the first SSO login, so `!last_sign_in_at` is false even for brand-new
      // users). It should be added once the server exposes a real first-login
      // flag (e.g. Devise sign_in_count) — spec Open Q8.
      is_impersonated: Boolean(whoami.is_impersonated),
      session_bootstrap_ms: Date.now() - appLoadedAt,
    });
  }, [posthog, whoami]);
}

function identifyPropertiesFromWhoami(whoami: Whoami): Properties {
  const properties: Properties = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  Object.entries(whoamiToIdentifyPropertyKeys).forEach(
    ([whoamiKey, propertyKey]) => {
      if (!propertyKey) {
        return;
      }
      if (typeof whoami[whoamiKey as keyof Whoami] === 'string') {
        properties[propertyKey] = whoami[whoamiKey as keyof Whoami];
      }
    },
  );

  return properties;
}
